import {
  useAuthContext,
  useScenarioContext,
  useSendBirdContext,
} from '@inbox-demo-nx/contexts';
import useScenarioUtils from '@inbox-demo-nx/hooks-use-scenario-utils';
import { MessageNotificationAction } from '@inbox-demo-nx/types';
import { RowStack, Spacer } from '@inbox-demo-nx/ui';
import {
  AnimatingCircle,
  useAnimatingCircleContext,
} from '@inbox-demo-nx/ui-animating-circle';
import Button from '@inbox-demo-nx/ui-button';
import { ChannelCover } from '@inbox-demo-nx/ui-chat-common';
import analytics from '@inbox-demo-nx/utils-analytics';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { Text } from 'dripsy';
import { ComponentProps, useCallback } from 'react';
import { Platform, SafeAreaView, View, ViewStyle } from 'react-native';
import { Easing, Notifier } from 'react-native-notifier';

const Action = ({ style, ...props }: ComponentProps<typeof Button>) => (
  <Button
    style={
      typeof style === 'function'
        ? (...args) => [{ flex: 1 }, style(...args)]
        : [{ flex: 1 }, style]
    }
    size="small"
    variant="secondary"
    {...props}
  />
);

const NotificationComponent = ({
  title,
  description,
  channel,
  actions = [],
  navigateToChannel,
  transitionChannelState,
  scenarioKey,
}: {
  title: string;
  description: string;
  channel: SendBird.GroupChannel;
  actions?: MessageNotificationAction[];
  navigateToChannel: () => void;
  transitionChannelState: (state: string) => void;
  scenarioKey: string;
}) => {
  return (
    <SafeAreaView>
      <View
        style={[
          {
            borderRadius: 4,
            backgroundColor: '#F7F7F7',
            shadowOffset: { width: 0, height: 2 },
            shadowRadius: 6,
            shadowColor: 'rgba(0, 0, 0, 0.24)',
            shadowOpacity: 1,
            paddingHorizontal: 16,
            paddingVertical: 12,
            marginHorizontal: 12,
            marginTop: Platform.OS === 'web' ? 44 : 0,
          },
          Platform.OS === 'web' && ({ cursor: 'pointer' } as ViewStyle),
        ]}
      >
        <RowStack>
          <ChannelCover channel={channel} size={16} />
          <Spacer size={6} />
          <Text
            sx={{
              fontSize: 'smedium',
              lineHeight: 'smedium',
              fontWeight: '600',
              color: 'accent',
            }}
          >
            {title}
          </Text>
        </RowStack>
        <Spacer size={4} />
        <Text
          sx={{ fontSize: 'smedium', lineHeight: 'smedium' }}
          numberOfLines={2}
        >
          {description}
        </Text>
        {actions.length > 0 && (
          <RowStack style={{ marginTop: 12 }}>
            {actions.map(
              (
                { label, navigateToChannelOnPress, stateToTransitionToOnPress },
                index
              ) => {
                return (
                  <Action
                    key={label}
                    title={label}
                    style={{ marginLeft: index > 0 ? 6 : 0 }}
                    onPress={() => {
                      analytics.track('click_notification', {
                        demo_key: scenarioKey,
                        action: label,
                      });

                      Notifier.hideNotification();
                      if (navigateToChannelOnPress) {
                        navigateToChannel();
                      } else if (stateToTransitionToOnPress) {
                        transitionChannelState(stateToTransitionToOnPress);
                      }
                    }}
                  />
                );
              }
            )}
          </RowStack>
        )}
        <AnimatingCircle id="notification" />
      </View>
    </SafeAreaView>
  );
};

export default function useNewMessageNotification() {
  const { currentUser } = useAuthContext();
  const { sendbird } = useSendBirdContext();
  const scenario = useScenarioContext();
  const navigation = useNavigation<NavigationProp<any>>();
  const { setChannelState } = useScenarioUtils();
  const { setTargetId } = useAnimatingCircleContext();

  const showNewMessageNotification = useCallback(
    async ({
      title,
      description,
      targetChannelCustomType,
      actions,
    }: {
      title: string;
      description: string;
      targetChannelCustomType: string;
      actions?: MessageNotificationAction[];
    }) => {
      const listQuery =
        await sendbird.GroupChannel.createMyGroupChannelListQuery();
      listQuery.customTypesFilter = [targetChannelCustomType];
      listQuery.limit = 1;
      const [channel] = await listQuery.next();

      if (!channel || !currentUser) {
        return;
      }

      const { url: channelUrl } = channel;

      const navigateToChannel = async () => {
        const { index, routes } = navigation.getState();
        if (
          routes[index].name === 'Chat' &&
          routes[index].params?.channelUrl !== channelUrl
        ) {
          navigation.goBack();
        }
        navigation.navigate('Chat', { channelUrl });
      };

      const onNotificationPress = () => {
        analytics.track('click_notification', {
          demo_key: scenario.key,
          action: 'none',
        });
        navigateToChannel();
      };

      const transitionChannelState = (state: string) => {
        setChannelState({
          channelUrl,
          state,
          userId: currentUser.userId,
        });
      };

      Notifier.showNotification({
        title,
        description,
        Component: NotificationComponent,
        componentProps: {
          channel,
          actions,
          navigateToChannel,
          transitionChannelState,
          scenarioKey: scenario.key,
        },
        duration: 0,
        showAnimationDuration: 800,
        showEasing: Easing.out(Easing.ease),
        onPress: onNotificationPress,
        hideOnPress: true,
      });
      setTargetId('notification');
    },
    [
      sendbird.GroupChannel,
      currentUser,
      scenario.key,
      setTargetId,
      navigation,
      setChannelState,
    ]
  );

  return showNewMessageNotification;
}
